import { useState } from "react";
import "./RepoChecks.css";
import { Button } from "./Button";
import { Alert } from "./Card";
import { Input } from "./Field";
import { authErrText, send } from "../lib/auth";
import { tx } from "../i18n";

/* ══ 저장소별 필수 체크 — 채택 시안 Figma 26:2(⑤) ══
   **세 상태다**(0030, `docs/decisions/repository.md`): `null` = 설치 기본을 따름 · 배열 = 그 이름들 ·
   `[]` = 요구 없음. 화면이 이 셋을 라디오 셋으로 그대로 보인다 — 「빈 태그 입력」을 「요구 없음」으로 접으면
   실수로 다 지운 것과 일부러 비운 것이 같은 값이 된다. 그래서 「직접 정함」인데 이름이 없으면 저장을 막는다. */

export type Checks = string[] | null;
type Mode = "default" | "custom" | "none";

const modeOf = (c: Checks): Mode => (c === null ? "default" : c.length ? "custom" : "none");

/** 줄에 붙는 칩 글자 — 서버 값 그대로 읽는다. */
export function checksLabel(c: Checks): string {
  if (c === null) return tx("repoChecks.chipDefault");
  if (!c.length) return tx("repoChecks.chipNone");
  return tx("repoChecks.chip", { names: c.join(", ") });
}

export function RepoChecksChip({ value, open, onToggle }: { value: Checks; open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className={`rchk__chip${value === null ? " is-default" : ""}`}
      aria-expanded={open}
      onClick={onToggle}
    >
      {checksLabel(value)} {open ? "⌃" : "⌄"}
    </button>
  );
}

export function RepoChecksEditor({
  repoId,
  value,
  onSaved,
  onClose,
  save = (id, v) => send<{ requiredChecks: Checks }>("PATCH", `/admin/repos/${encodeURIComponent(id)}`, { requiredChecks: v }),
}: {
  repoId: string;
  value: Checks;
  onSaved: (v: Checks) => void;
  onClose: () => void;
  /** 패턴 페이지가 서버 없이 그리려고 바꿔 끼운다. */
  save?: (id: string, v: Checks) => Promise<{ requiredChecks: Checks }>;
}) {
  const [mode, setMode] = useState<Mode>(modeOf(value));
  const [names, setNames] = useState<string[]>(value ?? []);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const empty = mode === "custom" && !names.length && !draft.trim();

  function push() {
    const n = draft.trim();
    if (n && !names.includes(n)) setNames([...names, n]);
    setDraft("");
  }

  async function submit() {
    const pending = draft.trim();
    const list = pending && !names.includes(pending) ? [...names, pending] : names;
    const v: Checks = mode === "default" ? null : mode === "none" ? [] : list;
    setBusy(true);
    setErr(null);
    try {
      // 서버가 다듬은 값(공백 · 겹침)을 그린다 — 보낸 값을 그리지 않는다.
      const r = await save(repoId, v);
      onSaved(r.requiredChecks);
      onClose();
    } catch (e) {
      setErr(authErrText(e));
    } finally {
      setBusy(false);
    }
  }

  const radio = (m: Mode, label: string, desc?: string) => (
    <label className="rchk__radio">
      <input type="radio" name={`rchk-${repoId}`} checked={mode === m} onChange={() => setMode(m)} />
      <span>{label}</span>
      {desc ? <span className="muted">{desc}</span> : null}
    </label>
  );

  return (
    <div className="rchk__edit">
      <p className="caption">{tx("repoChecks.lead")}</p>
      {radio("default", tx("repoChecks.default"), tx("repoChecks.defaultDesc"))}
      {radio("custom", tx("repoChecks.custom"))}
      {mode === "custom" ? (
        <div className="rchk__tags">
          {names.map((n) => (
            <span key={n} className="b-badge rchk__tag">
              {n}
              <button
                type="button"
                className="rchk__x"
                aria-label={tx("repoChecks.removeName", { name: n })}
                onClick={() => setNames(names.filter((x) => x !== n))}
              >
                ✕
              </button>
            </span>
          ))}
          <Input
            aria-label={tx("repoChecks.input")}
            value={draft}
            placeholder={tx("repoChecks.inputPh")}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                push();
              }
              if (e.key === "Backspace" && !draft && names.length) setNames(names.slice(0, -1));
            }}
          />
        </div>
      ) : null}
      {radio("none", tx("repoChecks.none"), tx("repoChecks.noneDesc"))}
      {empty ? <Alert tone="warning">{tx("repoChecks.empty")}</Alert> : null}
      {err ? <Alert tone="error">{err}</Alert> : null}
      <div className="rchk__acts">
        <Button size="sm" onClick={onClose}>
          {tx("common.action.cancel")}
        </Button>
        <Button size="sm" variant="primary" loading={busy} disabled={empty} onClick={submit}>
          {tx("common.action.save")}
        </Button>
      </div>
    </div>
  );
}
